import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { toast } from 'sonner';
import * as XLSX from 'xlsx';
import { ColumnDef } from '@tanstack/react-table';
import { FileSpreadsheet } from 'lucide-react';
import apiService from '../services/api.service';
import { cn } from '../lib/utils';
import { Button } from '../components/ui/button';
import { Alert, AlertDescription } from '../components/ui/alert';
import { StatusDot } from '../components/ui/status-dot';
import { PageHeader, Toolbar } from '../components/ui/page-header';
import { DataTable } from '../components/data-table/DataTable';
import { FilterChip } from '../components/data-table/FilterChip';
import { SearchField } from '../components/data-table/SearchField';
import { ExportMenu } from '../components/ExportMenu';
import { TableExportData, slugify } from '../lib/tableExport';
import { RegistrationSheet } from '../components/registrations/RegistrationSheet';
import {
  CHOICE_TYPES, CustomField, Registration, formatCustomResponse, hasValue, paymentLabelKey, paymentTone,
} from '../lib/registrations';
import { useRealtime } from '../contexts/RealtimeContext';
import { useEvent } from '../contexts/EventContext';

// Inscriptions › Réponses (refonte 2026-09-27) : une ligne par inscription
// confirmée, une colonne par question d'équipe, les réponses des participants
// dans le panneau. L'ancien écran empilait une carte par QUESTION : on y
// trouvait « qui a répondu quoi » à une question, jamais « qu'a répondu cette
// équipe » — or c'est la seconde lecture qu'on fait en préparant les dossards.
// L'export « une feuille par question » est conservé : c'est lui qui sert à
// commander les t-shirts par taille.

const REGISTRATIONS_KEY = (eventId: string) => ['registrations', eventId] as const;

export const RegistrationResponsesContent: React.FC<{ eventId: string; embedded?: boolean }> = ({ eventId, embedded = false }) => {
  const { t } = useTranslation();
  const { selectedEvent } = useEvent();

  const settingsQuery = useQuery({
    queryKey: ['registration-settings-fields', eventId],
    queryFn: async () => ((await apiService.getRegistrationSettings(eventId)).data.custom_fields || []) as CustomField[],
  });
  const registrationsQuery = useQuery({
    queryKey: REGISTRATIONS_KEY(eventId),
    queryFn: async () => ((await apiService.getEventRegistrations(eventId)).data || []) as Registration[],
  });
  useRealtime(['registration', 'team'], () => registrationsQuery.refetch());

  const fields = useMemo(() => settingsQuery.data ?? [], [settingsQuery.data]);
  const registrations = useMemo(() => registrationsQuery.data ?? [], [registrationsQuery.data]);
  const teamFields = useMemo(() => fields.filter((f) => (f.scope ?? 'team') === 'team').sort((a, b) => a.order - b.order), [fields]);
  // Une colonne par question d'équipe à laquelle au moins une inscription a
  // répondu : une question ajoutée après coup ne crée pas une colonne vide.
  const answeredTeamFields = useMemo(
    () => teamFields.filter((f) => registrations.some((r) => hasValue(r.team_custom_fields?.[f.id]))),
    [teamFields, registrations],
  );

  // ----- Filtres -----
  const [query, setQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState<string[]>([]);
  const [paymentFilter, setPaymentFilter] = useState<string[]>([]);
  const [answerFilters, setAnswerFilters] = useState<Record<string, string[]>>({});

  const categoryOptions = useMemo(() => {
    const m = new Map<string, { label: string; count: number }>();
    registrations.forEach((r) => {
      const k = r.category_id ?? 'none';
      const e = m.get(k) ?? { label: r.category_name ?? t('teams.noCategory'), count: 0 };
      e.count += 1; m.set(k, e);
    });
    return Array.from(m.entries()).map(([value, e]) => ({ value, ...e })).sort((a, b) => a.label.localeCompare(b.label));
  }, [registrations, t]);

  const paymentOptions = useMemo(() => {
    const m = new Map<string, number>();
    registrations.forEach((r) => { const k = r.payment_status ?? 'unknown'; m.set(k, (m.get(k) ?? 0) + 1); });
    return Array.from(m.entries()).map(([value, count]) => ({ value, label: t(paymentLabelKey(value)), count }));
  }, [registrations, t]);

  // Filtres par réponse : seulement les questions à choix, aux valeurs formatées
  // comme dans la colonne (une case cochée et sa colonne disent le même mot).
  const choiceFields = useMemo(() => answeredTeamFields.filter((f) => CHOICE_TYPES.has(f.type) || (f.options?.length ?? 0) > 0), [answeredTeamFields]);
  const answerOptions = useCallback((f: CustomField) => {
    const m = new Map<string, number>();
    registrations.forEach((r) => {
      const v = r.team_custom_fields?.[f.id];
      if (!hasValue(v)) return;
      const s = formatCustomResponse(v);
      m.set(s, (m.get(s) ?? 0) + 1);
    });
    return Array.from(m.entries()).map(([value, count]) => ({ value, label: value, count })).sort((a, b) => b.count - a.count);
  }, [registrations]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return registrations.filter((r) => {
      if (categoryFilter.length && !categoryFilter.includes(r.category_id ?? 'none')) return false;
      if (paymentFilter.length && !paymentFilter.includes(r.payment_status ?? 'unknown')) return false;
      for (const [fieldId, values] of Object.entries(answerFilters)) {
        if (values.length && !values.includes(formatCustomResponse(r.team_custom_fields?.[fieldId]))) return false;
      }
      if (!q) return true;
      const hay = [
        r.team_name, r.category_name, r.primary_contact_first_name, r.primary_contact_last_name, r.primary_contact_email,
        ...r.participants.flatMap((p) => [p.first_name, p.last_name, p.email]),
        ...Object.values(r.team_custom_fields ?? {}).map(formatCustomResponse),
      ].filter(Boolean).join(' ').toLowerCase();
      return hay.includes(q);
    });
  }, [registrations, query, categoryFilter, paymentFilter, answerFilters]);

  // ----- Panneau -----
  const [sheetId, setSheetId] = useState<string | null>(null);
  const sheet = registrations.find((r) => r.id === sheetId) ?? null;
  const idx = filtered.findIndex((r) => r.id === sheetId);
  useEffect(() => { setSheetId(null); }, [eventId]);

  // ----- Exports -----
  const exportData = (rows: Registration[]): TableExportData => ({
    filename: `inscriptions-${slugify(selectedEvent?.name ?? eventId)}`,
    title: t('registrations.tabs.responses'),
    subtitle: selectedEvent?.name,
    headers: [
      t('registrations.list.colTeam'), t('teams.category'), t('registrations.list.colContact'), t('common.email'),
      t('registrations.list.colParticipants'), t('registrations.list.colPayment'), t('registrations.registrationDate'),
      ...answeredTeamFields.map((f) => f.label),
    ],
    rows: rows.map((r) => [
      r.team_name, r.category_name ?? '', `${r.primary_contact_first_name} ${r.primary_contact_last_name}`.trim(), r.primary_contact_email,
      String(r.participants.length), t(paymentLabelKey(r.payment_status)), new Date(r.created_at).toLocaleDateString(),
      ...answeredTeamFields.map((f) => formatCustomResponse(r.team_custom_fields?.[f.id])),
    ]),
  });

  // « Une feuille par question », équipe ET participants : le fichier qu'on
  // transmet au fournisseur de t-shirts, inchangé dans sa forme.
  const exportByQuestion = () => {
    const perQuestion = new Map<string, { team: string; response: string }[]>();
    const label = (id: string) => fields.find((f) => f.id === id)?.label ?? id;
    const push = (id: string, team: string, v: any) => {
      if (!hasValue(v)) return;
      const k = label(id);
      if (!perQuestion.has(k)) perQuestion.set(k, []);
      perQuestion.get(k)!.push({ team, response: formatCustomResponse(v) });
    };
    filtered.forEach((r) => {
      Object.entries(r.team_custom_fields ?? {}).forEach(([id, v]) => push(id, r.team_name, v));
      r.participants.forEach((p) => Object.entries(p.custom_fields ?? {}).forEach(([id, v]) => push(id, `${p.first_name} ${p.last_name} (${r.team_name})`, v)));
    });
    if (perQuestion.size === 0) { toast.info(t('registrations.list.noAnswersToExport')); return; }
    const wb = XLSX.utils.book_new();
    const used = new Set<string>();
    perQuestion.forEach((responses, question) => {
      const ws = XLSX.utils.aoa_to_sheet([[question], [], [t('registrations.list.colTeamOrParticipant'), t('registrations.list.colAnswer')], ...responses.map((x) => [x.team, x.response])]);
      ws['!cols'] = [{ wch: 40 }, { wch: 30 }];
      // Excel : 31 caractères max, pas de doublon, pas de `[]:*?/\`.
      let name = question.replace(/[[\]:*?/\\]/g, ' ').substring(0, 31).trim() || 'Question';
      let n = 2;
      while (used.has(name)) name = `${name.substring(0, 28)} ${n++}`;
      used.add(name);
      XLSX.utils.book_append_sheet(wb, ws, name);
    });
    XLSX.writeFile(wb, `${slugify(`inscriptions-questions-${selectedEvent?.name ?? eventId}`)}.xlsx`);
  };

  // ----- Colonnes -----
  const columns = useMemo<ColumnDef<Registration, any>[]>(() => [
    {
      id: 'team', accessorFn: (r) => r.team_name, header: () => t('registrations.list.colTeam'),
      sortingFn: (a, b) => a.original.team_name.localeCompare(b.original.team_name, undefined, { sensitivity: 'base' }),
      cell: ({ row }) => <span className="font-semibold text-foreground">{row.original.team_name}</span>,
    },
    { id: 'category', accessorFn: (r) => r.category_name ?? '', header: () => t('teams.category'), cell: ({ getValue }) => <span className="text-muted-foreground">{getValue() || '—'}</span> },
    {
      id: 'contact', accessorFn: (r) => `${r.primary_contact_last_name} ${r.primary_contact_first_name}`, header: () => t('registrations.list.colContact'),
      cell: ({ row }) => (
        <div className="min-w-0">
          <div>{row.original.primary_contact_first_name} {row.original.primary_contact_last_name}</div>
          <div className="truncate text-xs text-muted-foreground">{row.original.primary_contact_email}</div>
        </div>
      ),
    },
    { id: 'participants', accessorFn: (r) => r.participants.length, header: () => t('registrations.list.colParticipants'), cell: ({ getValue }) => <span className="tabular-nums">{getValue()}</span> },
    {
      id: 'payment', accessorFn: (r) => r.payment_status ?? '', header: () => t('registrations.list.colPayment'),
      cell: ({ row }) => <StatusDot tone={paymentTone(row.original.payment_status)} label={t(paymentLabelKey(row.original.payment_status))} className="text-muted-foreground" />,
    },
    {
      id: 'created', accessorFn: (r) => r.created_at, header: () => t('registrations.registrationDate'),
      cell: ({ getValue }) => <span className="whitespace-nowrap text-muted-foreground">{new Date(getValue()).toLocaleDateString()}</span>,
    },
    ...answeredTeamFields.map<ColumnDef<Registration, any>>((f) => ({
      id: `q-${f.id}`, accessorFn: (r) => formatCustomResponse(r.team_custom_fields?.[f.id]), header: () => f.label,
      cell: ({ getValue }) => <span className="line-clamp-1 max-w-[220px] text-muted-foreground">{getValue() || '—'}</span>,
    })),
  ], [t, answeredTeamFields]);

  const filtering = !!(query || categoryFilter.length || paymentFilter.length || Object.values(answerFilters).some((v) => v.length));

  return (
    <div className={cn('space-y-5', !embedded && 'p-4 md:p-8', sheet && 'min-[1600px]:pr-[544px]', 'transition-[padding] duration-200')}>
      {!embedded && (
        <PageHeader eyebrow={selectedEvent?.name} title={t('registrations.tabs.responses')} meta={t('registrations.list.count', { count: registrations.length })} />
      )}

      <Toolbar>
        <SearchField value={query} onChange={setQuery} placeholder={t('registrations.list.searchPlaceholder')} />
        {categoryOptions.length > 1 && <FilterChip label={t('teams.category')} options={categoryOptions} selected={categoryFilter} onChange={setCategoryFilter} />}
        {paymentOptions.length > 1 && <FilterChip label={t('registrations.list.colPayment')} options={paymentOptions} selected={paymentFilter} onChange={setPaymentFilter} />}
        {choiceFields.map((f) => (
          <FilterChip key={f.id} label={f.label} options={answerOptions(f)} selected={answerFilters[f.id] ?? []} onChange={(v) => setAnswerFilters((prev) => ({ ...prev, [f.id]: v }))} />
        ))}
        {filtering && <span className="text-xs text-muted-foreground">{t('teams.filteredCount', { shown: filtered.length, total: registrations.length })}</span>}
        <div className="ml-auto flex items-center gap-2">
          {embedded && <span className="text-sm text-muted-foreground">{t('registrations.list.count', { count: registrations.length })}</span>}
          <Button variant="outline" size="sm" onClick={exportByQuestion} disabled={!filtered.length}><FileSpreadsheet size={16} />{t('registrations.list.exportByQuestion')}</Button>
          <ExportMenu getData={() => exportData(filtered)} disabled={!filtered.length} />
        </div>
      </Toolbar>

      {registrationsQuery.isError && (
        <Alert variant="destructive">
          <AlertDescription className="flex items-center justify-between gap-4">
            {t('registrations.list.loadError')}
            <Button variant="outline" size="sm" onClick={() => registrationsQuery.refetch()}>{t('common.retry')}</Button>
          </AlertDescription>
        </Alert>
      )}

      <DataTable
        ariaLabel={t('registrations.tabs.responses')}
        columns={columns}
        data={filtered}
        getRowId={(r) => r.id}
        loading={registrationsQuery.isLoading || settingsQuery.isLoading}
        onRowClick={(r) => setSheetId(r.id)}
        activeRowId={sheet?.id ?? null}
        initialSorting={[{ id: 'created', desc: true }]}
        empty={registrations.length === 0 ? t('registrations.noRegistrations') : t('registrations.list.noMatch')}
        renderCard={(r) => (
          <div className="py-3">
            <div className="flex items-baseline justify-between gap-3">
              <span className="truncate font-semibold">{r.team_name}</span>
              <StatusDot tone={paymentTone(r.payment_status)} label={t(paymentLabelKey(r.payment_status))} className="text-xs text-muted-foreground" />
            </div>
            <div className="mt-1 flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground">
              {r.category_name && <span>{r.category_name}</span>}
              <span>{r.primary_contact_first_name} {r.primary_contact_last_name}</span>
              <span>{t('registrations.list.participantsShort', { count: r.participants.length })}</span>
            </div>
          </div>
        )}
      />

      <RegistrationSheet
        open={!!sheet}
        registration={sheet}
        fields={fields}
        onClose={() => setSheetId(null)}
        onPrev={idx > 0 ? () => setSheetId(filtered[idx - 1].id) : undefined}
        onNext={idx >= 0 && idx < filtered.length - 1 ? () => setSheetId(filtered[idx + 1].id) : undefined}
      />
    </div>
  );
};

// Route `/events/:eventId/registrations` (liens anciens, notifications).
const EventRegistrations: React.FC = () => {
  const { eventId } = useParams<{ eventId: string }>();
  if (!eventId) return null;
  return <RegistrationResponsesContent eventId={eventId} />;
};

export default EventRegistrations;
